import { cx } from '@linaria/core';
import DOMPurify from 'dompurify';
import { ChangeEvent, ChangeEventHandler, FocusEventHandler, KeyboardEventHandler, memo, useCallback, useEffect, useState, useTransition } from 'react';
import { SecureStateType } from '../../../constants/enums';
import { URL_PASSWORD_RESET } from '../../../constants/urls';
import { locales } from '../../../locales/en';
import { login } from '../../../mfcLogic/auth';
import { trackLoginInteraction } from '../../../mfcLogic/lens';
import { isLevelBasic, isLevelPremiumOrHigher } from '../../../mfcLogic/userProperties/level';
import { closeDrawer, openAuthenticationDrawer, openSignupDrawer, openTokensDrawer } from '../../../state/modals/state.drawer';
import { onInputBlur, onInputFocus } from '../../../state/state.layout';
import { getPromotionalBannerText } from '../../../utilities/bannerText';
import { getCookie } from '../../../utilities/cookies';
import { routerPush } from '../../../utilities/router';
import Button from '../../Buttons/Button';
import { AppStateType, useAppStateSelector } from '../../Hooks/useAppState';
import { useSecureState } from '../../Hooks/useSecureState';
import { useUserSelector } from '../../Hooks/useUser';
import Eye from '../../Icons/Eye';
import EyeOff from '../../Icons/EyeOff';
import Alert from '../../common/Alert';
import { StyledDivider } from '../../common/StyledDivider';
import TextField from '../../common/TextField';
import { InlinePulseLoading } from '../InlineLoading';
import Link from '../Link';
import AgeGateHeader from '../Modals/common/AgeGateHeader';
import ThirdPartyProviders from './ThirdPartyProviders';
import { eyeIconStyle, smallEyeIconStyle, validationWarningStyles } from './shared';
import "./Login.linaria.module.css!=!./Login?./Login.linaria.module.css?css=H4sIAAAAAAAAA2VU0a6jIBR83//YZPfBRlrb27ofQ6gCosCxeKq2ZP9909tQgX0xmTAjMMyZHUNDmzMl09CxHnYp9AIsFpN68pqQcf3z90cgKByIxhRF7HL3deQm8N2Bku4E/f3pka9YMK2krRtukbvAmR+UwHSD/ewX1WJXk7L8GRa/NCUdE+dl8A1ocPXM3K+iMKIpFuassvJ3oGqgar9gVaXIj6xtlZUFwliftqtoTgk2sj/KDH4EV0AEU++rTcQcJdexgqHJoBear0WrHG9Qga0b0Hdjg2yYKFnucrJHDzN3QsNSsztCWL9MlOC6qFn7Vk2jZo/69b+w3B9oK5VF4w1bi7dJx7LcjiVvlIhltTPPoDfMSWXDVcg+uj9QvjZ4vqYosYukfsV8nvPDFrFkSCRDLtFcYCrQiUDnAqdklyrwQqV+LMK884WO2UmAM7UFyyN7ot9+UG7OKXnn2c0NX1OUv7KDJSiunBJ5LuXplMG3ZnFsrF+fwO/CYKTIf09IoZCbKZsT0SSSDfr+PqESj6IBi9xipnsl+1FVoEUG86erkjmPA7XBuBfSMKl2FLJKURKmMj7QeINDxTOYH6jcHd3WJXpI9hjyPb7TVEZRitk6Z7+j9KGbgXZ7Y/qTH2FS78flmqGaPykSjpJlfwHXes0RuSumkTXKyrrckWPiRVIs8F+xvNyIW+VFidzeYDqMkd/9gXYDPkBFjVAljfAMjZPBEPpvt+IO6spEscGgeDuWSA7xXH1QEEQN8g/QZ0ThbQYAAA=="
const captionStyles = "atm_c8_1skhajo";
const dividerStyles = "atm_c8_itk1lt atm_r3_1h6ojuz atm_vy_1osqo2v";
const warningStyles = "atm_7l_1haf8wk";
const helpStyles = "atm_lo_i2wt44";
const rootStyles = "atm_le_1tcgj5g atm_ar_1bp4okc atm_ks_1wugsn5 atm_9s_1txwivl atm_j3_dgintm";
const loginStyles = "atm_gq_1fwxnve atm_lo_exct8b atm_le_exct8b atm_lk_exct8b atm_ll_exct8b atm_t9_glywfm atm_vy_1osqo2v";
const subjectStyles = "atm_gq_exct8b";
const formStyles = "atm_vy_1osqo2v atm_9s_1txwivl atm_ar_vrvcex atm_be_1g80g66";
const signupStyles = "atm_h_1h6ojuz atm_fc_1h6ojuz atm_9s_1txwivl atm_vy_1osqo2v atm_le_1y44olf atm_c8_1fwxnve";
const forgotPasswordStyles = "atm_r3_1h6ojuz atm_lo_idpfg4 atm_le_1pqo34e atm_lk_idpfg4 atm_ll_idpfg4 atm_vy_1osqo2v atm_c8_1fwxnve";
const inputContainerStyles = "atm_mk_h2mmj6 atm_vy_1osqo2v";
const newAccountStyles = "atm_c8_1fwxnve atm_fr_1w29ord";
const rootEmbeddedStyles = "atm_vy_1osqo2v";
const rootPaddingLargeStyles = "atm_lo_1tcgj5g";
const rootPaddingSmallStyles = "atm_lo_1fwxnve";
const alertWrapperStyles = "atm_j3_hktyoi atm_gq_exct8b atm_gz_1wugsn5 atm_h0_1wugsn5";
const bannerContentStyles = "atm_vy_1osqo2v";
const purchaseButtonStyles = "atm_h3_exct8b";
const onPurchaseTokens = () => openTokensDrawer({
  onBack: () => {
    openAuthenticationDrawer();
  }
});
interface HelperTextProps {
  error?: string;
  username?: string;
}
const HelperText = (props: HelperTextProps) => {
  const {
    error,
    username
  } = props;
  const unverified = useAppStateSelector(AppStateType.APP, 'unverified');
  return error ? <div className={validationWarningStyles}>
            *{' '}
            <span data-testid="Login__ValidationError" dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(error, {
        USE_PROFILES: {
          html: true
        }
      })
    }} />
            {username && !unverified && <div data-testid="Login__UnableToLoginNotice" className={helpStyles}>
                    If you are unable to log in, you may{' '}
                    <>
                        <a target="_blank" rel="noopener noreferrer" data-mfc-ext="1" href={`https://www.myfreecams.com/php/account.php?request_login_link=1&load=app&style=app&username=${username}`} className={warningStyles}>
                            click here to receive a login link to your email
                        </a>{' '}
                        or{' '}
                    </>
                    <a target="_blank" rel="noopener noreferrer" data-mfc-ext="1" href="https://www.myfreecams.com/php/account.php?request=password&load=app&style=app" className={warningStyles}>
                        click here to reset your password
                    </a>
                    .
                </div>}
        </div> : null;
};
type MaskedPasswordInputProps = {
  error?: string;
  username?: string;
  small?: boolean;
  onEnter: KeyboardEventHandler<HTMLInputElement>;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
};
const MaskedPasswordInput = memo((props: MaskedPasswordInputProps) => {
  const [masked, setMasked] = useState(true);
  const {
    error,
    onEnter,
    onChange,
    onFocus,
    onBlur,
    small,
    username
  } = props;
  const onToggleMasked = useCallback(() => setMasked(!masked), [masked]);
  return <div className={inputContainerStyles}>
            <TextField small={small} label={locales.login.password} data-testid="Login__PasswordInput" type={masked ? 'password' : 'text'} autoComplete="current-password" onChange={onChange} onKeyUp={onEnter} onFocus={onFocus} onBlur={onBlur} error={!!error} helperText={<HelperText error={error} username={username} />} required fullWidth spaced />
            {masked ? <Eye onClick={onToggleMasked} className={small ? smallEyeIconStyle : eyeIconStyle} /> : <EyeOff onClick={onToggleMasked} className={small ? smallEyeIconStyle : eyeIconStyle} />}
        </div>;
});
const onOpenSignupDrawer = () => {
  openSignupDrawer();
};
interface LoginProps {
  embedded?: boolean;
  subject?: string;
  redirect?: string;
  onSignup?(): void;
}
const Login = memo((props: LoginProps) => {
  const isAuthenticated = useAppStateSelector(AppStateType.APP, 'authenticated');
  const level = useUserSelector('me', 'level');
  const myUsername = useUserSelector('me', 'name');
  const {
    ageGateActive
  } = useSecureState(SecureStateType.AGE_VERIFICATION);
  const [error, setError] = useState<string>();
  const [username, setUsername] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [isPending, startTransition] = useTransition();
  const {
    subject,
    redirect,
    onSignup
  } = props;
  const isAuthAndBasic = isLevelBasic(level);
  const isAuthAndPremiumOrHigher = isLevelPremiumOrHigher(level);
  useEffect(() => {
    setUsername(getCookie('username'));
  }, []);
  const onUsername = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  }, []);
  const onPassword = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  }, []);
  const onLogin = useCallback((event?) => {
    event?.preventDefault();
    trackLoginInteraction(false);
    startTransition(async () => {
      const {
        errmsg
      } = await login(username, password);
      if (errmsg) {
        setError(errmsg);
        return;
      }
      if (redirect) routerPush(redirect); // Router.push(redirect, redirect, { shallow: true })
      window.location.reload();
    });
  }, [username, password, redirect]);
  const onEnter = useCallback(event => {
    const {
      key
    } = event;
    if (key !== 'Enter') return;
    event.preventDefault();
    onLogin();
  }, [onLogin]);
  useEffect(() => {
    // if we somehow arrived here while logged in as
    // a premium member or higher close this drawer
    if (isAuthAndPremiumOrHigher) closeDrawer();
  }, [isAuthAndPremiumOrHigher]);
  return <div className={props.embedded ? rootEmbeddedStyles : cx(rootStyles, ageGateActive ? rootPaddingSmallStyles : rootPaddingLargeStyles)}>
            {!props.embedded && ageGateActive && <AgeGateHeader />}
            {!ageGateActive && isAuthAndBasic && <div data-testid="Login__BasicMemberBanner" className={alertWrapperStyles}>
                    <Alert severity={'success'} icon={false} centerText>
                        <div className={bannerContentStyles}>
                            Welcome back, <strong>{myUsername}</strong>! <br />{' '}
                            <br />
                            {getPromotionalBannerText(false)}
                        </div>
                    </Alert>
                    <Button onClick={onPurchaseTokens} size="large" className={purchaseButtonStyles}>
                        {!isAuthenticated ? locales.account.create : locales.account.purchaseTokens}
                    </Button>
                </div>}
            {!isAuthenticated && <>
                    {!!subject && <div data-testid="Authentication__Prompt" className={cx(alertWrapperStyles, subjectStyles)}>
                            <Alert severity={'info'}>
                                {locales.login.mustBeLoggedIn}
                                {subject}.
                            </Alert>
                        </div>}
                    <ThirdPartyProviders embedded={props.embedded} />
                    <div className={dividerStyles} style={{
        paddingTop: props.embedded ? 6 : 12,
        paddingBottom: props.embedded ? 12 : 18
      }}>
                        <StyledDivider />
                    </div>
                    <form noValidate data-testid="Login__Form" className={formStyles}>
                        <TextField small={props?.embedded} label={locales.login.username} data-testid="Login__UsernameInput" onChange={onUsername} onFocus={onInputFocus} onBlur={onInputBlur} autoComplete="username" value={username || ''} error={!!error} required fullWidth spaced />
                        <MaskedPasswordInput small={props?.embedded} error={error} username={username} onEnter={onEnter} onChange={onPassword} onFocus={onInputFocus} onBlur={onInputBlur} />
                        <Button data-testid="Login__LoginButton" onClick={onLogin} disabled={!username || !password || isPending} size={props.embedded ? 'medium' : 'large'} className={loginStyles}>
                            {!isAuthenticated && isPending && <InlinePulseLoading active />}
                            {locales.login.login}
                        </Button>
                        <div data-testid="Login__ForgotPasswordLink" className={forgotPasswordStyles}>
                            <Link inline mfc href={URL_PASSWORD_RESET}>
                                {locales.login.forgotPassword}
                            </Link>
                        </div>
                        <div data-testid="Login__RegistrationLink" className={signupStyles}>
                            <span className={cx('typography caption', captionStyles)}>
                                {locales.login.noAccount}
                                <Link onClick={onSignup ? onSignup : onOpenSignupDrawer} actionLink mfc className={newAccountStyles}>
                                    {locales.login.createFreeAccount}
                                </Link>
                            </span>
                        </div>
                    </form>
                </>}
        </div>;
});
export default Login;